/**
 * QuestionCard — interactive HITL card for `awaiting_input` runs.
 *
 * Renders the ask_user question with one-tap option chips + free text, then
 * POSTs to /brain/resume via engine.resumeRun. Answered/expired states render
 * read-only.
 */

import { useState } from "react";
import { HelpCircle, Send, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@flowstate/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { QuestionPayload } from "../../engine/cortexStreamReducer";

interface QuestionCardProps {
  runId: string;
  question: QuestionPayload;
  answered?: { approved: boolean; value?: string };
  onAnswer: (runId: string, opts: { approved?: boolean; value?: string }) => void;
  disabled?: boolean;
}

export function QuestionCard({ runId, question, answered, onAnswer, disabled }: QuestionCardProps) {
  const [freeText, setFreeText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = (value?: string, approved = true) => {
    setSubmitting(true);
    try {
      onAnswer(runId, { approved, value });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 backdrop-blur-sm overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-primary/20">
        <HelpCircle className="h-4 w-4 text-primary" />
        <span className="text-xs font-medium text-foreground/90">
          Cortex needs your input
        </span>
        {answered && (
          <span className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
            {answered.approved ? (
              <><CheckCircle2 className="h-3 w-3 text-green-500" /> Answered</>
            ) : (
              <><XCircle className="h-3 w-3 text-destructive" /> Rejected</>
            )}
          </span>
        )}
      </div>
      <div className="px-3 py-2.5 space-y-2.5">
        <p className="text-sm leading-snug text-foreground">{question.question}</p>

        {!answered && (
          <>
            {question.options && question.options.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {question.options.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    disabled={disabled || submitting}
                    onClick={() => submit(opt)}
                    className={cn(
                      "text-xs px-3 py-1.5 rounded-full border border-primary/30 bg-background/50",
                      "hover:bg-primary/15 hover:border-primary/50 transition-colors",
                      "disabled:opacity-50",
                    )}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
            {question.allowFreeText !== false && (
              <div className="flex gap-1.5">
                <Input
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  placeholder="Type your answer…"
                  className="h-8 text-xs bg-background/50"
                  disabled={disabled || submitting}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && freeText.trim()) submit(freeText.trim());
                  }}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 px-2"
                  disabled={!freeText.trim() || disabled || submitting}
                  onClick={() => submit(freeText.trim())}
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            <button
              type="button"
              disabled={disabled || submitting}
              onClick={() => submit(undefined, false)}
              className="text-[10px] text-muted-foreground/70 hover:text-destructive transition-colors"
            >
              Dismiss / reject
            </button>
          </>
        )}

        {answered && answered.value && (
          <p className="text-xs text-muted-foreground">
            You answered: <span className="text-foreground">{answered.value}</span>
          </p>
        )}
      </div>
    </div>
  );
}
